import { useEffect, useMemo, useRef, useState } from 'react';
import { extractMentionedAdminUserIds, recalculateMentionsAfterTextChange, } from '../mentionUtils';
import { buildBackspaceDeletionResult, buildOverlayContent, buildSelectionResult, resolveMentionContext, } from './useMentionInputHelpers';
export var useMentionInput = function (_a) {
    var text = _a.text, onTextChange = _a.onTextChange, mentionableUsers = _a.mentionableUsers, onMentionedAdminUserIdsChange = _a.onMentionedAdminUserIdsChange;
    var wrapperRef = useRef(null);
    var textareaRef = useRef(null);
    var overlayRef = useRef(null);
    var mentionListRef = useRef(null);
    var _b = useState(''), mentionQuery = _b[0], setMentionQuery = _b[1];
    var _c = useState(null), mentionStart = _c[0], setMentionStart = _c[1];
    var _d = useState(0), selectedMentionIndex = _d[0], setSelectedMentionIndex = _d[1];
    var _e = useState([]), activeMentions = _e[0], setActiveMentions = _e[1];
    var closeMentionDropdown = function () {
        setMentionQuery('');
        setMentionStart(null);
        setSelectedMentionIndex(0);
    };
    var updateMentionContext = function (nextText, cursorPosition) {
        var context = resolveMentionContext(nextText, cursorPosition);
        if (!context) {
            closeMentionDropdown();
            return;
        }
        setMentionQuery(context.query);
        setMentionStart(context.start);
        setSelectedMentionIndex(0);
    };
    var handleTextInputChange = function (nextText, cursorPosition) {
        onTextChange(nextText);
        var nextMentions = recalculateMentionsAfterTextChange(text, nextText, activeMentions);
        setActiveMentions(nextMentions);
        onMentionedAdminUserIdsChange === null || onMentionedAdminUserIdsChange === void 0 ? void 0 : onMentionedAdminUserIdsChange(extractMentionedAdminUserIds(nextMentions));
        updateMentionContext(nextText, cursorPosition);
    };
    var filteredMentionableUsers = mentionStart !== null
        ? mentionableUsers.filter(function (user) {
            return user.fullName.toLowerCase().includes(mentionQuery.toLowerCase());
        })
        : [];
    var renderOverlayText = useMemo(function () {
        return buildOverlayContent(text, activeMentions);
    }, [activeMentions, text]);
    var selectMention = function (user) {
        var _a, _b;
        if (mentionStart === null)
            return;
        var cursorPosition = (_b = (_a = textareaRef.current) === null || _a === void 0 ? void 0 : _a.selectionStart) !== null && _b !== void 0 ? _b : text.length;
        var _c = buildSelectionResult({
            text: text,
            mentionStart: mentionStart,
            cursorPosition: cursorPosition,
            user: user,
            activeMentions: activeMentions,
        }), nextText = _c.nextText, nextMentions = _c.nextMentions, nextCursor = _c.nextCursor;
        onTextChange(nextText);
        setActiveMentions(nextMentions);
        onMentionedAdminUserIdsChange === null || onMentionedAdminUserIdsChange === void 0 ? void 0 : onMentionedAdminUserIdsChange(extractMentionedAdminUserIds(nextMentions));
        closeMentionDropdown();
        window.requestAnimationFrame(function () {
            if (!textareaRef.current)
                return;
            textareaRef.current.selectionStart = nextCursor;
            textareaRef.current.selectionEnd = nextCursor;
            textareaRef.current.focus();
        });
    };
    var handleBackspace = function (event) {
        var cursor = event.currentTarget.selectionStart;
        var selectionEnd = event.currentTarget.selectionEnd;
        if (cursor !== selectionEnd)
            return false;
        var deletionResult = buildBackspaceDeletionResult({
            text: text,
            activeMentions: activeMentions,
            cursor: cursor,
        });
        if (!deletionResult)
            return false;
        event.preventDefault();
        onTextChange(deletionResult.nextText);
        setActiveMentions(deletionResult.nextMentions);
        onMentionedAdminUserIdsChange === null || onMentionedAdminUserIdsChange === void 0 ? void 0 : onMentionedAdminUserIdsChange(extractMentionedAdminUserIds(deletionResult.nextMentions));
        closeMentionDropdown();
        window.requestAnimationFrame(function () {
            if (!textareaRef.current)
                return;
            textareaRef.current.selectionStart = deletionResult.nextCursor;
            textareaRef.current.selectionEnd = deletionResult.nextCursor;
        });
        return true;
    };
    var handleKeyDown = function (event) {
        if (event.key === 'Backspace' && handleBackspace(event)) {
            return true;
        }
        if (filteredMentionableUsers.length > 0 && mentionStart !== null) {
            var selectedUser = filteredMentionableUsers[selectedMentionIndex];
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                setSelectedMentionIndex(function (index) {
                    return index === filteredMentionableUsers.length - 1 ? 0 : index + 1;
                });
                return true;
            }
            if (event.key === 'ArrowUp') {
                event.preventDefault();
                setSelectedMentionIndex(function (index) {
                    return index === 0 ? filteredMentionableUsers.length - 1 : index - 1;
                });
                return true;
            }
            if (event.key === 'Tab') {
                event.preventDefault();
                setSelectedMentionIndex(function (index) {
                    return index === filteredMentionableUsers.length - 1 ? 0 : index + 1;
                });
                return true;
            }
            if ((event.key === 'Enter' && !event.shiftKey) ||
                event.key === ' ') {
                event.preventDefault();
                if (!selectedUser)
                    return true;
                selectMention(selectedUser);
                return true;
            }
        }
        return false;
    };
    var handleInputScroll = function () {
        if (!overlayRef.current || !textareaRef.current)
            return;
        overlayRef.current.scrollTop = textareaRef.current.scrollTop;
    };
    var handleInputClick = function (event) {
        updateMentionContext(text, event.currentTarget.selectionStart);
    };
    useEffect(function () {
        if (!text) {
            setActiveMentions([]);
            onMentionedAdminUserIdsChange === null || onMentionedAdminUserIdsChange === void 0 ? void 0 : onMentionedAdminUserIdsChange([]);
            closeMentionDropdown();
        }
    }, [text, onMentionedAdminUserIdsChange]);
    useEffect(function () {
        var handleClickOutside = function (event) {
            if (!wrapperRef.current)
                return;
            if (!wrapperRef.current.contains(event.target)) {
                closeMentionDropdown();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return function () { return document.removeEventListener('mousedown', handleClickOutside); };
    }, []);
    useEffect(function () {
        if (!mentionListRef.current || mentionStart === null || filteredMentionableUsers.length === 0) {
            return;
        }
        var selectedOption = mentionListRef.current.querySelector("[data-mention-option-index=\"" + selectedMentionIndex + "\"]");
        selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.scrollIntoView({ block: 'nearest' });
    }, [filteredMentionableUsers.length, mentionStart, selectedMentionIndex]);
    return {
        wrapperRef: wrapperRef,
        textareaRef: textareaRef,
        overlayRef: overlayRef,
        mentionListRef: mentionListRef,
        mentionStart: mentionStart,
        selectedMentionIndex: selectedMentionIndex,
        filteredMentionableUsers: filteredMentionableUsers,
        renderOverlayText: renderOverlayText,
        closeMentionDropdown: closeMentionDropdown,
        setSelectedMentionIndex: setSelectedMentionIndex,
        handleTextInputChange: handleTextInputChange,
        selectMention: selectMention,
        handleKeyDown: handleKeyDown,
        handleInputScroll: handleInputScroll,
        handleInputClick: handleInputClick,
    };
};
